import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import {
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { FormErrors } from "../../components/Login/FormErrors";
import { userService } from "_services/user.service";
import { EMAIL_REGEX } from "__helpers/constants";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";

/**
 * Manager "Groups" panel: an expandable accordion of the caller manager's own group
 * hierarchy — their assigned group at the root, expandable to reveal nested child
 * groups and directly-attached facilities (recursively). Facilities are display-only.
 * Data: GET /api/org-admin/my-group-tree (group resolved server-side from the caller's
 * ROLE_MANAGER row; returns a GroupTreeDTO { id, groupName, groupType, hierarchyLevel,
 * active, facilities[], children[] }).
 */

// Group levels with UI label + accent colour (kept consistent with the Management Hierarchy screen).
const LEVEL_META = {
  EXECUTIVE: { label: "Executive", color: "#6a1b9a" },
  SVPO: { label: "SVPO group", color: "#00706f" },
  VPO: { label: "VPO group", color: "#2e7d32" },
  ADMIN: { label: "Admin group", color: "#c47f17" },
};
const FACILITY_COLOR = "#607d8b";
const levelLabel = (t) => (LEVEL_META[t] || {}).label || t || "Group";
const levelColor = (t) => (LEVEL_META[t] || {}).color || "#00706f";

// Stable key for a group node's expand state (ids are unique across the tree).
const nodeKey = (node) => `g-${node.id}`;

export default class OrgGroups extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tree: null,
      loading: true,
      error: "",
      expanded: {}, // { [nodeKey]: true }
      // change-manager dialog
      cmOpen: false,
      cmGroup: null,        // the target group node
      cmCurrent: [],        // current managers of that group
      cmForm: { firstName: "", lastName: "", email: "" },
      cmErrors: { firstName: "", lastName: "", email: "" },
      cmSaving: false,
      cmLoading: false,
    };
  }

  componentDidMount() {
    this.fetchTree();
  }

  notify = (message, type) => userService.showNotification({ title: "Change Manager", message, type });

  fetchTree = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const r = await fetch(apiPath.orgAdminMyGroupTree, { method: "GET", headers: generalAuthTokenHeader() });
      if (r.ok) {
        const data = await r.json();
        // Root starts expanded so the manager sees one level immediately.
        const expanded = data && data.id ? { [nodeKey(data)]: true } : {};
        this.setState({ tree: data, loading: false, expanded });
      } else {
        this.setState({ loading: false, error: "Could not load your groups." });
      }
    } catch (e) {
      this.setState({ loading: false, error: "Server error while loading groups." });
    }
  };

  toggle = (key) => this.setState((s) => ({ expanded: { ...s.expanded, [key]: !s.expanded[key] } }));

  // ----- change-manager dialog (descendant groups only) -----
  openChangeManager = (node) => {
    this.setState({
      cmOpen: true,
      cmGroup: node,
      cmCurrent: [],
      cmForm: { firstName: "", lastName: "", email: "" },
      cmErrors: { firstName: "", lastName: "", email: "" },
      cmSaving: false,
      cmLoading: true,
    });
    fetch(`${apiPath.orgAdminGroups}/${node.id}/managers`, { method: "GET", headers: generalAuthTokenHeader() })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => this.setState({ cmCurrent: Array.isArray(data) ? data : [], cmLoading: false }))
      .catch(() => this.setState({ cmCurrent: [], cmLoading: false }));
  };

  closeChangeManager = () => this.setState({ cmOpen: false });

  cmField = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ cmForm: { ...s.cmForm, [name]: value } }));
  };

  submitChangeManager = async () => {
    const { cmForm, cmGroup } = this.state;
    const errors = { firstName: "", lastName: "", email: "" };
    let ok = true;
    if (!cmForm.firstName.trim()) { errors.firstName = "First name is required"; ok = false; }
    if (!cmForm.lastName.trim()) { errors.lastName = "Last name is required"; ok = false; }
    if (!cmForm.email.trim() || !EMAIL_REGEX.test(cmForm.email.trim())) { errors.email = "Valid email is required"; ok = false; }
    if (!ok) { this.setState({ cmErrors: errors }); return; }

    this.setState({ cmSaving: true });
    try {
      const body = {
        firstName: cmForm.firstName.trim(),
        lastName: cmForm.lastName.trim(),
        email: cmForm.email.trim(),
      };
      const r = await fetch(`${apiPath.orgAdminGroups}/${cmGroup.id}/managers`, {
        method: "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(body),
      });
      if (r.ok) {
        this.notify("Manager updated", "success");
        this.setState({ cmOpen: false, cmSaving: false });
      } else {
        const t = await r.text();
        this.notify(t || "Could not change manager", "danger");
        this.setState({ cmSaving: false });
      }
    } catch (e) {
      this.notify("Server error", "danger");
      this.setState({ cmSaving: false });
    }
  };

  currentManagerLabel = () => {
    const active = this.state.cmCurrent.filter((m) => m.status === "ACTIVE");
    if (!active.length) return "None assigned";
    return active
      .map((m) => `${(m.firstName || "").trim()} ${(m.lastName || "").trim()}`.trim() || m.email)
      .join(", ");
  };

  renderFacility = (f) => (
    <div key={`f-${f.id}`} className="og-row og-facility" style={{ borderLeftColor: FACILITY_COLOR }}>
      <span className="og-spacer" />
      <span className="og-fac-icon" role="img" aria-label="facility">🏥</span>
      <span className="og-name">{f.facilityName || `#${f.facilityId}`}</span>
    </div>
  );

  renderNode = (node, depth) => {
    if (!node || !node.id) return null;
    const key = nodeKey(node);
    const children = node.children || [];
    const facilities = node.facilities || [];
    const hasBelow = children.length > 0 || facilities.length > 0;
    const isOpen = !!this.state.expanded[key];
    const inactive = node.active === false;

    return (
      <div key={key} className="og-branch">
        <div
          className={`og-row og-group${inactive ? " og-inactive" : ""}`}
          style={{ borderLeftColor: levelColor(node.groupType), paddingLeft: 12 + depth * 22 }}
          onClick={() => hasBelow && this.toggle(key)}
          role={hasBelow ? "button" : undefined}
        >
          <span className="og-caret">{hasBelow ? (isOpen ? "▾" : "▸") : ""}</span>
          <span className="og-name og-group-name">{node.groupName}</span>
          <span className="og-level" style={{ color: levelColor(node.groupType) }}>
            {levelLabel(node.groupType)}
          </span>
          <span className="og-count">{facilities.length} fac.</span>
          {inactive && <span className="og-badge-inactive">Inactive</span>}
          {depth > 0 && (
            <span
              className="og-change-mgr"
              title="Change manager"
              role="button"
              onClick={(e) => { e.stopPropagation(); this.openChangeManager(node); }}
            >
              <i className="material-icons">manage_accounts</i>
            </span>
          )}
        </div>

        {isOpen && hasBelow && (
          <div className="og-children">
            {children.map((c) => this.renderNode(c, depth + 1))}
            {facilities.length > 0 && (
              <div style={{ paddingLeft: 12 + (depth + 1) * 22 }}>
                {facilities.map(this.renderFacility)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  render() {
    const { tree, loading, error } = this.state;
    return (
      <GridContainer>
        <Card className={"advert-cover"}>
          <GridItem xs={12} sm={12} md={12}>
            <CardHeader color="success" className={"cusCardheader cusCardTitle"}>
              <h4>Groups</h4>
            </CardHeader>
          </GridItem>

          <div className={"custom-container"}>
            {/* Legend */}
            <div className="og-legend">
              {Object.keys(LEVEL_META).map((t) => (
                <span key={t} className="og-legend-item">
                  <i style={{ background: levelColor(t) }} /> {levelLabel(t)}
                </span>
              ))}
              <span className="og-legend-item">
                <i style={{ background: FACILITY_COLOR }} /> Facility
              </span>
            </div>

            <div className="og-tree">
              {loading ? (
                <div className="og-empty"><CircularProgress size={26} /></div>
              ) : error ? (
                <div className="og-empty">{error}</div>
              ) : tree && tree.id ? (
                this.renderNode(tree, 0)
              ) : (
                <div className="og-empty">No group is assigned to you yet.</div>
              )}
            </div>
          </div>
        </Card>

        {/* Change Manager dialog */}
        <Dialog open={this.state.cmOpen} maxWidth="sm" fullWidth onClose={this.closeChangeManager} className="add-modal">
          <DialogTitle className="add-modal-title">
            Change Manager{this.state.cmGroup ? ` — ${this.state.cmGroup.groupName}` : ""}
            <IconButton className="closeButton" aria-label="close" onClick={this.closeChangeManager}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div style={{ fontSize: "0.82rem", color: "#6b6969", margin: "6px 0 14px" }}>
              Current manager:{" "}
              <strong style={{ color: "#2b2b2b" }}>
                {this.state.cmLoading ? "…" : this.currentManagerLabel()}
              </strong>
            </div>
            <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", marginBottom: 6 }}>
              Assign a new manager (replaces the current one)
            </div>
            <Box display="flex" mb={1} mt={1}>
              <Box width={1 / 2} pr={1}>
                <TextField fullWidth name="firstName" value={this.state.cmForm.firstName} onChange={this.cmField} label="First Name *" />
                <FormErrors show={true} formErrors={this.state.cmErrors} fieldName="firstName" />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField fullWidth name="lastName" value={this.state.cmForm.lastName} onChange={this.cmField} label="Last Name *" />
                <FormErrors show={true} formErrors={this.state.cmErrors} fieldName="lastName" />
              </Box>
            </Box>
            <Box mb={1}>
              <TextField fullWidth name="email" value={this.state.cmForm.email} onChange={this.cmField} label="Email *" />
              <FormErrors show={true} formErrors={this.state.cmErrors} fieldName="email" />
            </Box>
            <div style={{ fontSize: "0.76rem", color: "#8a8a8a", marginTop: 4 }}>
              If the email belongs to an existing user they'll be assigned; otherwise a new account is created and invited by email.
            </div>
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button type="button" className="btn1" onClick={this.submitChangeManager} disabled={this.state.cmSaving}>
              {this.state.cmSaving && <CircularProgress size={24} className="buttonProgress" />}
              Assign Manager
            </Button>
            <Button onClick={this.closeChangeManager} className="btn2" color="primary">Cancel</Button>
          </DialogActions>
        </Dialog>

        <style>{OG_STYLES}</style>
      </GridContainer>
    );
  }
}

// Scoped styles for the accordion (prefixed og- to avoid collisions).
const OG_STYLES = `
.og-legend { display:flex; flex-wrap:wrap; gap:16px; margin:6px 0 14px; font-size:0.82rem; color:#555; }
.og-legend-item { display:inline-flex; align-items:center; }
.og-legend-item i { display:inline-block; width:12px; height:12px; border-radius:3px; margin-right:6px; }

.og-tree { padding:4px 0 16px; }
.og-empty { text-align:center; color:#8a8a8a; padding:40px 0; font-size:0.9rem; }

.og-row {
  display:flex; align-items:center; gap:8px; padding:10px 12px; margin:4px 0;
  background:#fff; border:1px solid #eceef0; border-left:4px solid #00706f; border-radius:6px;
}
.og-group { cursor:pointer; }
.og-group:hover { background:#f7f9f9; }
.og-facility { background:#fafbfc; border-left-width:4px; padding:8px 12px; }

.og-caret { width:14px; color:#90a4ae; font-size:0.85rem; flex:0 0 auto; }
.og-spacer { width:14px; flex:0 0 auto; }
.og-fac-icon { font-size:0.85rem; flex:0 0 auto; }
.og-name { font-size:0.88rem; color:#37474f; }
.og-group-name { font-weight:600; color:#2b2b2b; }
.og-level { font-size:0.74rem; font-weight:600; margin-left:6px; }
.og-count { color:#8a8a8a; font-size:0.74rem; margin-left:auto; white-space:nowrap; }
.og-change-mgr { display:inline-flex; align-items:center; cursor:pointer; color:#00706f; margin-left:4px; }
.og-change-mgr:hover { color:#004f4e; }
.og-change-mgr .material-icons { font-size:19px; }
.og-fac-type { font-size:0.62rem; font-weight:700; letter-spacing:0.04em; color:#90a4ae; margin-left:auto; }

.og-badge-inactive {
  background:#eceff1; color:#78909c; font-size:0.6rem; font-weight:700; text-transform:uppercase;
  letter-spacing:0.04em; padding:1px 6px; border-radius:8px; border:1px solid #cfd8dc; margin-left:8px;
}
.og-inactive .og-group-name, .og-inactive .og-count { opacity:0.55; }
`;
